// @flow

import { Component } from '../jsti/core';
import { toCamel, uppercaseFirst } from '../classes/Helpers';
import DataLayerEvent from '../classes/DataLayer/Events/GA4/DataLayerEvent';
import Notifications from '../constants/Notifications';
import type { EventData, EventName } from '../classes/DataLayer/types';
import Cookie from '../classes/Cookie';
import Cookies from '../constants/Cookies';

type DispatchHandler = {
  data: Object,
  event: EventName,
  timeoutId: number,
};

export default class EventDispatcher extends Component {
  #maxAllowedDispatchersPerPage = 1;
  #dispatchHandlers: Array<Object> = [];
  #eventQueue = [];

  /**
   * Getter for the queue
   */
  get eventQueue(): Array<EventData> {
    return this.#eventQueue;
  }

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    const components: Array<HTMLElement> = [...document.body.querySelectorAll('[data-component="event-dispatcher"]')];
    if (components.length > this.#maxAllowedDispatchersPerPage) {
      throw new Error('A page can only have one event dispatcher');
    }

    this.addListener(Notifications.COOKIES_CHANGED, this.setCookieConsent);
    this.addListener(Notifications.DISPATCH_EVENT, this.onEventRequest);

    this.addQueueListeners();
  }

  /**
   * Handles incoming event requests
   * Only dispatches a event when it does not receive a new event within its time
   */
  onEventRequest(data: EventData): void {
    if (data.shouldQueue) {
      this.#eventQueue.push(data);

      setTimeout(() => {
        this.dispatchQueue();
      }, 500);
      return;
    }

    this.dispatchQueue();

    const handler: DispatchHandler = this.#dispatchHandlers
      .find((handler: DispatchHandler) => handler.event === data.event && handler.data === data.data);

    if (handler) {
      this.#dispatchHandlers.splice(this.#dispatchHandlers.indexOf(handler), 1);
      clearTimeout(handler.timeoutId);
    }

    this.#dispatchHandlers.push({
      data: data.data,
      event: data.event,
      timeoutId: setTimeout(() => {
        this.dispatch(data);
      }, data.delay)
    });
  }

  /**
   * Dispatch the event
   */
  dispatch(data: EventData): boolean {
    let event: ?DataLayerEvent = null;
    try {
      const eventName = uppercaseFirst(toCamel(data.event));
      let EventClass;
      if (data.GA4) {
        EventClass = require(`../classes/DataLayer/Events/GA4/${eventName}`).default;
      } else if (data.Insider) {
        EventClass = require(`../classes/DataLayer/Events/Insider/${eventName}`).default;
      }
      event = new EventClass(data.data, data.element || null);
    } catch {
      throw new Error('Event was not found');
    }

    if (!event || !event.validate()) {
      console.warn(`Could not dispatch ${data.event} event, because of invalid data`);
      return false;
    }
    event.dispatch();
    return true;
  }

  /**
   * google tag manager datalayer push method
   */
  gtag() {
    window.dataLayer.push(arguments);
  }

  /**
   * sets the cookie consent to the datalayer
   */
  setCookieConsent(): void {
    let consent = 'denied';
    if (Cookie.get(Cookies.TRACKING) === '1') {
      consent = 'granted';
    }

    this.gtag('consent', 'update', {
      ad_storage: consent,
      ad_user_data: consent,
      ad_personalization: consent,
      analytics_storage: 'granted',
      personalization_storage: consent,
      functionality_storage: 'granted',
      security_storage: 'granted'
    });
  }

  /**
   * filters the queue by event and dispatch all items
   */
  dispatchQueue(): void {
    if (!this.#eventQueue.length > 0) return;

    let eventsNames = this.#eventQueue.map((event: EventData) => event.event);
    eventsNames = eventsNames.filter((value: Object, index: number) => eventsNames.indexOf(value) === index);
    eventsNames.forEach((eventName: string) => {
      const queue = [];
      let GA4: boolean = false;

      this.#eventQueue.forEach((event: EventData) => {
        if (event.event === eventName) {
          queue.push(event.data);
          GA4 = event.GA4;
        }
      });

      this.dispatch({ event: eventName, data: queue, GA4 });
    });
    this.#eventQueue = [];
  }

  /**
   * Add listeners to dispatch the queue on notifications
   */
  addQueueListeners(): void {
    addEventListener('beforeunload', () => {
      this.dispatchQueue();
    });

    try {
      const dispatchQueueDataset: string = this.el.dataset.dispatchQueueOn;
      if (dispatchQueueDataset && dispatchQueueDataset !== '') {
        const notifications: Array<string> = JSON.parse(dispatchQueueDataset);
        notifications.forEach((notification: string) => {
          this.addListener(notification, this.dispatchQueue);
        });
      }
    } catch {
      throw new Error('Invalid dataset');
    }
  }
}
